import { OmniScrollArea } from "@/components/artifacts/OmniScrollArea.tsx";
import { OmniPickerRow } from "@/components/artifacts/omni/OmniPickerRow.tsx";
import { useRoutineToggle } from "@/components/routines/useRoutineToggle.ts";
import { getTriggerIcon } from "@/components/routines/triggerIcon.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useRoutines } from "@/hooks/useRoutines.ts";
import { getTriggerLabel, routineRowKey } from "@/shared/routines.ts";
import type { RoutineResp } from "@/types/routines.ts";

/**
 * The routines chooser, sized for the omnibox card.
 *
 * Every routine the user has, on or off. Routines are already running by the
 * time this card is raised, so the list is the only place the whole set is
 * visible — filtering it to the ones already on would hide the routines the
 * user has never heard of, which are exactly the ones the card exists to offer.
 *
 * Each row carries when it fires as well as what it's called. A name alone
 * doesn't answer the question the user is actually being asked — whether to
 * leave this thing running — and the description that would answer it in full
 * costs a second line the card can't spend. The trigger is the smallest thing
 * that still supports the decision; the canvas card has the room for the rest.
 *
 * Toggling saves as it's made, so "Done" ends the step rather than committing
 * anything, and a dismissed card keeps whatever was already changed. Unlike the
 * canvas rows these don't navigate: the omnibox floats over the thread and has
 * nowhere to push a routine's detail page, so the row is the switch and nothing
 * else.
 */
export const RoutinesOmniPickerBody = () => {
  const { routines, isLoading } = useRoutines();

  // Skeleton rows rather than a spinner: they hold the height the list is about
  // to take, so the card doesn't grow under the cursor as the routines land.
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 rounded-md border p-3">
        <Skeleton className="h-3.5 w-36" />
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-3.5 w-40" />
      </div>
    );
  }

  if (routines.length === 0) {
    return <p className="text-xs text-muted-foreground">No routines are available yet.</p>;
  }

  return (
    <div className="overflow-hidden rounded-md border">
      {/* Whole rows, never a sliced one: four 36px rows on a phone, where this
          card shares the screen with the keyboard, and six on a desktop. Deep
          enough for the shipped set to show without moving, shallow enough that a
          user with twenty of their own can't take the whole thread with them. */}
      <OmniScrollArea className="max-h-[144px] md:max-h-[216px]">
        {routines.map((routine) => (
          <RoutineToggleRow key={routineRowKey(routine)} routine={routine} />
        ))}
      </OmniScrollArea>
    </div>
  );
};

/**
 * One routine as a dense row: what it is, when it runs, and whether it's on.
 *
 * The check is held in the layout when the routine is off rather than removed,
 * so names don't shift sideways as rows are toggled, and the row dims instead —
 * the same on/off idiom as the calendars card, which sits in the same chrome.
 */
const RoutineToggleRow = ({ routine }: { routine: RoutineResp }) => {
  const { setEnabled, isSaving } = useRoutineToggle(routine);
  const TriggerIcon = getTriggerIcon(routine.triggerConfig.type);

  return (
    <OmniPickerRow
      checked={routine.enabled}
      onToggle={() => void setEnabled(!routine.enabled)}
      disabled={isSaving}
      label={routine.name}
      labelTitle={routine.description || undefined}
      // The trigger icon leads the descriptor rather than sitting beside the
      // name, so the left edge is the checkbox and the name alone.
      qualifier={
        <span className="flex items-center gap-1">
          <TriggerIcon className="size-3.5 shrink-0" />
          <span className="truncate">{getTriggerLabel(routine.triggerConfig)}</span>
        </span>
      }
      ariaState={getTriggerLabel(routine.triggerConfig)}
      statsigName="omni-picker-routine-toggle"
    />
  );
};
